import { Box, Skeleton } from "@mui/material";
import { type FC, useState } from "react";
import { type RandomRange, randomBetween, randomCount } from "./skeletonVariants";

/** The narrowest a detail card gets before the grid stacks the cards in one column. */
const CARD_MIN_WIDTH = "16rem";
/** How the detail page varies per mount: the title's width, the card and line counts, and each heading's and line's width (%). */
const DETAIL_RANGES = {
	titleWidth: [30, 50],
	cards: [2, 3],
	headingWidth: [30, 50],
	lines: [3, 5],
	lineWidth: [45, 90],
} as const satisfies Record<string, RandomRange>;

/** A record's page: an avatar with a title and subtitle over a grid of detail cards, each a heading and its lines. */
export const DetailSkeletonVariant: FC = () => {
	const [layout] = useState(() => ({
		titleWidth: randomBetween(DETAIL_RANGES.titleWidth),
		cards: Array.from({ length: randomCount(DETAIL_RANGES.cards) }, () => ({
			headingWidth: randomBetween(DETAIL_RANGES.headingWidth),
			lineWidths: Array.from({ length: randomCount(DETAIL_RANGES.lines) }, () => randomBetween(DETAIL_RANGES.lineWidth)),
		})),
	}));

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
				<Skeleton variant="circular" sx={{ width: (theme) => theme.spacing(8), height: (theme) => theme.spacing(8), flexShrink: 0 }} />
				<Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
					<Skeleton variant="text" width={`${layout.titleWidth}%`} sx={{ typography: "h5" }} />
					<Skeleton variant="text" width="25%" />
				</Box>
			</Box>
			<Box sx={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${CARD_MIN_WIDTH}, 1fr))`, gap: 2 }}>
				{layout.cards.map((card, cardIndex) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: placeholder cards are a fixed set of bars; nothing reorders them.
					<Box key={cardIndex} sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
						<Skeleton variant="text" width={`${card.headingWidth}%`} sx={{ typography: "subtitle1" }} />
						{card.lineWidths.map((lineWidth, lineIndex) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: placeholder lines are a fixed set of bars; nothing reorders them.
							<Skeleton key={lineIndex} variant="text" width={`${lineWidth}%`} />
						))}
					</Box>
				))}
			</Box>
		</Box>
	);
};
